import { Vector3, Box3, OrthographicCamera, Quaternion } from "three";
import { IQuick3DCamera, TCameraUpdateEvent } from "./quick3d-camera-interface";
import { makeArrayOfObjects } from "../utils/camera-utils";

export interface IOrthographicQuick3DCameraArguments {
  /**
   * ratio of view with to view height. usually calculated from dimensions of the render target
   */
  aspect?: number;

  /**
   * max view distance of the camera. objects further than this will be clipped or not rendered at all
   */
  far?: number;

  /**
   * nearest view distance of the camera. objects closer than this will be clipped or not rendered at all
   */
  near?: number;

  /**
   * used to calculate bounds of the frustum. for example, a camera with an aspect of 1.5 and viewsize of 300 will have the following values:
   *
   * top = 150, bottom = -150, left = -225, right = 225
   */
  viewSize?: number;
}

export interface IOrthographicFitParameters {
  /**
   * ratio of fit area that should be left clear from bottom towards up. for example,
   * setting to 1 may leave the bottom half of the view space blank, while 0 leaves none
   *
   * common value range is [0, 1]
   */
  bottomPadding?: number;

  /**
   * determines how far back from the area center the camera should be placed after fitting.
   * a good value is usually in the 1000's, but depends on the scene being rendered
   */
  distanceToPlaceCamera?: number;

  /**
   * ratio of viewing space that should be left clear from left towards right.
   * for example, setting to 1 may leave the left half of the view space blank, while 0 leaves none
   *
   * common value range is [0, 1]
   */
  leftPadding?: number;

  /**
   * ratio of viewing space that should be left clear from right towards left.
   * for example, setting to 1 may leave the right half of the view space blank, while 0 leaves none
   *
   * common value range is [0, 1]
   */
  rightPadding?: number;

  /**
   * ratio of viewing space that should be left clear from top towards bottom.
   * for example, setting to 1 may leave the top half of the view space blank, while 0 leaves none
   *
   * common value range is [0, 1]
   */
  topPadding?: number;
}

/**
 * used to render scenes with orthographic projection
 *
 * [orthographic projection](https://en.wikipedia.org/wiki/3D_projection#Orthographic_projection)
 *
 * [THREE.OrthographicCamera documentation](https://threejs.org/docs/#api/en/cameras/OrthographicCamera)
 */
export class Quick3DOrthographicCamera
  extends OrthographicCamera
  implements IQuick3DCamera
{
  private _aspect: number;
  private _viewSize: number;

  //stored to prevent reallocation of memory
  private boxSize: Vector3;
  private worldXAxis: Vector3;
  private worldYAxis: Vector3;
  private worldZAxis: Vector3;
  private transformedBox: Box3;
  private transformedBoxCorners: Array<Vector3>;
  private updateEvent: TCameraUpdateEvent;

  public get aspect(): number {
    return this._aspect;
  }
  public set aspect(ratio: number) {
    this.left = this._viewSize * -0.5 * ratio;
    this.right = this._viewSize * 0.5 * ratio;
    this.top = this._viewSize * 0.5;
    this.bottom = this._viewSize * -0.5;

    this._aspect = ratio;
    this.updateProjectionMatrix();
  }

  public get height(): number {
    return this.top - this.bottom;
  }
  public get width(): number {
    return this.right - this.left;
  }
  public get viewSize(): number {
    return this.viewSize;
  }
  public set viewSize(size: number) {
    this.left = size * -0.5 * this.aspect;
    this.right = size * 0.5 * this.aspect;
    this.top = size * 0.5;
    this.bottom = size * -0.5;

    this._viewSize = size;
    this.updateProjectionMatrix();
  }

  /**
   * @param cameraArguments.aspect defaults to 1
   * @param cameraArguments.far defaults to 10000
   * @param cameraArguments.near defaults to 1
   * @param cameraArguments.viewSize defaults to 300
   */
  public constructor(
    cameraArguments: IOrthographicQuick3DCameraArguments = {}
  ) {
    const {
      aspect = 1,
      far = 10000,
      near = 1,
      viewSize = 300,
    } = cameraArguments;
    super(
      viewSize * -0.5 * aspect,
      viewSize * 0.5 * aspect,
      viewSize * 0.5,
      viewSize * -0.5,
      near,
      far
    );
    this._aspect = aspect;
    this._viewSize = viewSize;

    this.worldXAxis = new Vector3();
    this.worldYAxis = new Vector3();
    this.worldZAxis = new Vector3();
    this.transformedBox = new Box3();
    this.transformedBoxCorners = makeArrayOfObjects<Vector3>(8, Vector3);
    this.boxSize = new Vector3();

    this.updateEvent = {
      type: "cameraUpdateEvent",
      camera: this,
    } as TCameraUpdateEvent;
  }
  /**
   * re-orients the camera such that the center of the box is in the middle of the screen, and all bounds plus any defined padding fit within the frustum.
   *
   * [THREE.Box3 documentation](https://threejs.org/docs/#api/en/math/Box3)
   * @param area 3D volume the camera should attempt to fit around. for example, the bounding box of a model, or of an entire scene of objects.
   * @param fitParameters.bottomPadding defaults to 0
   * @param fitParameters.leftPadding defaults to 0
   * @param fitParameters.rightPadding defaults to 0
   * @param fitParameters.topPadding defaults to 0
   */
  public fit(area: Box3, fitParameters: IOrthographicFitParameters = {}): void {
    const {
      bottomPadding = 0,
      leftPadding = 0,
      rightPadding = 0,
      topPadding = 0,
      distanceToPlaceCamera = 0,
    } = fitParameters;

    if (area.isEmpty()) {
      return;
    }

    this.zoom = 1;
    this.updateProjectionMatrix();

    this.transformedBoxCorners[0]
      .set(area.min.x, area.min.y, area.min.z)
      .project(this);
    this.transformedBoxCorners[1]
      .set(area.min.x, area.min.y, area.max.z)
      .project(this);
    this.transformedBoxCorners[2]
      .set(area.min.x, area.max.y, area.min.z)
      .project(this);
    this.transformedBoxCorners[3]
      .set(area.min.x, area.max.y, area.max.z)
      .project(this);
    this.transformedBoxCorners[4]
      .set(area.max.x, area.min.y, area.min.z)
      .project(this);
    this.transformedBoxCorners[5]
      .set(area.max.x, area.min.y, area.max.z)
      .project(this);
    this.transformedBoxCorners[6]
      .set(area.max.x, area.max.y, area.min.z)
      .project(this);
    this.transformedBoxCorners[7]
      .set(area.max.x, area.max.y, area.max.z)
      .project(this);

    this.transformedBox = new Box3();
    this.transformedBox.setFromPoints(this.transformedBoxCorners);
    this.transformedBox.getSize(this.boxSize);
    this.transformedBox.min.x -= leftPadding * this.boxSize.x * this.aspect;
    this.transformedBox.max.x += rightPadding * this.boxSize.x * this.aspect;
    this.transformedBox.min.y -= bottomPadding * this.boxSize.y;
    this.transformedBox.max.y += topPadding * this.boxSize.y;
    this.transformedBox.getSize(this.boxSize);

    // derived from how three js uses zoom to modify frustum
    const heightRatio = 0.5 * this.boxSize.y;
    const widthRatio = 0.5 * this.boxSize.x;

    this.zoom = 1.0 / Math.max(heightRatio, widthRatio);

    const scalar = distanceToPlaceCamera
      ? distanceToPlaceCamera
      : this.boxSize.length();
    this.matrixWorld.extractBasis(
      this.worldXAxis,
      this.worldYAxis,
      this.worldZAxis
    );
    const offset = this.worldZAxis.multiplyScalar(scalar);
    const target = area.getCenter(new Vector3());
    if (this.parent) {
      const inverseRotation = this.getWorldQuaternion(
        new Quaternion()
      ).invert();
      offset.applyQuaternion(inverseRotation);
      target
        .sub(this.parent.getWorldPosition(new Vector3()))
        .applyQuaternion(inverseRotation);
    }
    this.position.addVectors(offset, target);

    this.updateProjectionMatrix();
    this.updateMatrix();
  }

  /**
   * currently documented function for updating world matrices.
   * has performance issues, better to use updateWorldMatrix instead
   *
   * [see issue on git](https://github.com/mrdoob/three.js/pull/16292)
   *
   * overridden to emit cameraUpdateEvent event
   *
   * @param force perform calculations regardless if needed
   */
  public override updateMatrixWorld(force: boolean): void {
    if (this.matrixAutoUpdate) {
      this.updateMatrix();
    }

    if (this.matrixWorldNeedsUpdate || force) {
      if (!this.parent) {
        this.matrixWorld.copy(this.matrix);
      } else {
        this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
      }
      this.matrixWorldNeedsUpdate = false;
      force = true;

      this.dispatchEvent(this.updateEvent);
    }

    // update children
    const children = this.children;
    for (let i = 0, l = children.length; i < l; ++i) {
      children[i].updateMatrixWorld(force);
    }

    // this.matrixWorldInverse.getInverse(this.matrixWorld);
    this.matrixWorldInverse = this.matrixWorld.clone().invert();
  }

  /**
   * proposed version of updateMatrixWorld that exists but is undocumented
   *
   * [see issue on git](https://github.com/mrdoob/three.js/pull/16292)
   *
   * overridden to emit cameraUpdateEvent event
   * @param [updateParents] boolean
   * @param [updateChildren] boolean
   */
  public override updateWorldMatrix(
    updateParents = false,
    updateChildren = true
  ): void {
    this.dispatchEvent(this.updateEvent);
    super.updateWorldMatrix(updateParents, updateChildren);
  }

  /**
   * overridden to emit cameraUpdateEvent event
   */
  public override updateProjectionMatrix(): void {
    super.updateProjectionMatrix();
    this.dispatchEvent(this.updateEvent);
  }
}
